import React, { useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import {
  normalizeUsername,
  validateUsernameFormat,
  isUsernameTaken,
  USERNAME_TAKEN_MESSAGE,
  mapProfileUsernameError,
} from '../lib/usernameUtils';
import type { Profile } from '../types/database';
import { getAuthCallbackUrl } from '../lib/siteUrl';
import { mapAuthEmailError } from '../lib/authEmailErrors';
import { applyDashboardPrefsToDocument } from '../lib/dashboardPrefsApply';
import { readDashboardPrefsFromLocalStorage } from '../lib/dashboardPrefsStorage';
import { identityService } from '../services/identityService';
import { mapProfileToUser } from '../lib/mapProfileToUser';
import { claimReferralCode, claimReferralForSessionUser } from '../lib/claimReferral';
import { presenceService } from '../services/presenceService';
import type { Session, User as SupabaseUser } from '@supabase/supabase-js';

export interface User {
  id: string;
  username: string;
  displayName: string;
  email: string;
  bio: string;
  avatar?: string;
  coverUrl?: string;
  isSeller: boolean;
  sellerRating?: number;
  followers: number;
  following: number;
  verified: boolean;
  isAdmin: boolean;
  isEmployee: boolean;
  /** @district88 official account — shows Official tag instead of Employee */
  isOfficial: boolean;
  /** True when Connect can accept charges; used for listing publish gate. */
  stripeConnectReady?: boolean;
  /** Manager: operations & command center (see profiles.is_manager in Supabase) */
  isManager: boolean;
  badges: string[];
  styleTags: string[];
  reputationScore: number;
  availabilityStatus: 'online' | 'busy' | 'away' | 'offline' | 'open_to_chat';
  lastSeenAt?: string;
  location: string;
  website: string;
  pronouns: string;
  favoriteBrands: string[];
  shoeSize: string;
  // Identity System
  level: number;
  xpPoints: number;
  cultureScore: number;
  cultureTitle: string;
  styleArchetype: string;
  reputationTier: string;
  profileTheme: string;
  accountType: 'seller' | 'collector' | 'curator' | 'creator';
  activeStreak: number;
  totalSales: number;
  collectorScore?: number;
  collectorTier?: string;
  cultureMemberXp?: number;
  cultureMemberLevel?: number;
  trendHunterPoints?: number;
  districtPath?: string;
  lastIdentitySyncAt?: string;
  createdAt?: string;
  // Personal Story
  myStory: string;
  originStory: string;
  inspiration: string;
  stylePhilosophy: string;
  currentChapter: string;
  motto: string;
  whatD88Means: string;
  futureVision: string;
  favoriteQuote: string;
  // Goals + Dreams
  goals: string[];
  dreamBrand: string;
  dreamSneaker: string;
  dreamGrail: string;
  currentProject: string;
  biggestAmbition: string;
  dreamCollab: string;
  // Style Identity
  outfitAesthetic: string;
  mostWornPiece: string;
  signatureLook: string;
  favoriteColors: string[];
  styleEvolution: string;
  // Personality + Culture
  creativeInterests: string[];
  culturalInfluences: string;
  favoriteCity: string;
  playlistUrl: string;
  favoriteEra: string;
  communityValues: string;
  whatIStandFor: string;
  // Social + Connection
  socialLinks: Record<string, string>;
  openToCollab: boolean;
  openToBusiness: boolean;
  languages: string[];
  networkingInterests: string[];
  currentlyChasing: string;
  fitOfMonth: string;
  emailVerified: boolean;
  prefsPublicProfile: boolean;
  prefsShowVerifiedBadge: boolean;
  prefsShowSold: boolean;
  prefsShowLocation: boolean;
  prefsShowActivity: boolean;
  prefsDmFrom: 'everyone' | 'following' | 'off';
}

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoggedIn: boolean;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    username: string,
    displayName: string,
    accountType?: 'seller' | 'collector' | 'curator' | 'creator',
    referralCode?: string | null
  ) => Promise<{ error: string | null; needsEmailConfirmation?: boolean }>;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error: string | null; errorCode?: string | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: string | null }>;
  refreshProfile: () => Promise<void>;
  setAvailability: (status: User['availabilityStatus']) => Promise<void>;
  checkUsernameAvailable: (username: string) => Promise<boolean>;
  /** Re-send the signup confirmation email (same redirect as registration). */
  resendSignupConfirmation: (email: string) => Promise<{ error: string | null }>;
}

/** When the profiles row is slow or RLS blocks reads, still treat the user as signed in. */
function stubProfileFromSupabaseUser(su: SupabaseUser): Profile {
  const meta = (su.user_metadata || {}) as Record<string, unknown>;
  const email = su.email ?? '';
  const username = String(meta.username ?? email.split('@')[0] ?? 'user');
  const display_name = String(meta.display_name ?? meta.username ?? username);
  return {
    id: su.id,
    username,
    display_name,
    email,
    bio: '',
    avatar_url: null,
    cover_url: null,
    is_seller: false,
    seller_rating: null,
    followers_count: 0,
    following_count: 0,
    reputation_score: 0,
    badges: [],
    style_tags: [],
    verified: false,
    is_admin: false,
    is_employee: false,
    is_official_account: false,
    stripe_connect_ready: false,
    is_manager: false,
    availability_status: 'offline',
    location: '',
    website: '',
    pronouns: '',
    favorite_brands: [],
    shoe_size: '',
    my_story: '',
    origin_story: '',
    inspiration: '',
    style_philosophy: '',
    current_chapter: '',
    motto: '',
    what_d88_means: '',
    future_vision: '',
    favorite_quote: '',
    goals: [],
    dream_brand: '',
    dream_sneaker: '',
    dream_grail: '',
    current_project: '',
    biggest_ambition: '',
    dream_collab: '',
    outfit_aesthetic: '',
    most_worn_piece: '',
    signature_look: '',
    favorite_colors: [],
    style_evolution: '',
    creative_interests: [],
    cultural_influences: '',
    favorite_city: '',
    playlist_url: '',
    favorite_era: '',
    community_values: '',
    what_i_stand_for: '',
    social_links: {},
    open_to_collab: false,
    open_to_business: false,
    languages: [],
    networking_interests: [],
    level: 1,
    xp_points: 0,
    culture_score: 0,
    culture_title: 'Observer',
    style_archetype: '',
    reputation_tier: 'Standard',
    profile_theme: 'default',
    account_type: 'collector',
    prefs_public_profile: true,
    prefs_show_verified_badge: true,
    prefs_show_sold: true,
    prefs_show_location: true,
    prefs_show_activity: true,
    prefs_dm_from: 'everyone',
    currently_chasing: '',
    fit_of_month: '',
    active_streak: 0,
    total_sales: 0,
    created_at: su.created_at ?? new Date().toISOString(),
    updated_at: su.updated_at ?? new Date().toISOString(),
  };
}

export const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);
  /** Latest user for auth callbacks — avoids replacing a loaded ops profile with a stub on TOKEN_REFRESHED. */
  const userRef = React.useRef<User | null>(null);
  React.useEffect(() => {
    userRef.current = user;
  }, [user]);

  React.useEffect(() => {
    const uid = session?.user?.id;
    applyDashboardPrefsToDocument(readDashboardPrefsFromLocalStorage(uid));
  }, [session?.user?.id]);

  const fetchProfile = useCallback(async (userId: string, supabaseUser?: SupabaseUser): Promise<User | null> => {
    const applyVerified = (u: User) => {
      u.emailVerified = !!supabaseUser?.email_confirmed_at;
      return u;
    };

    const fallback = () =>
      supabaseUser ? applyVerified(mapProfileToUser(stubProfileFromSupabaseUser(supabaseUser))) : null;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error || !data) {
        if (error) console.warn('Profile fetch:', error.message);
        return fallback();
      }
      const profile = data as Profile & { last_identity_sync_at?: string | null };
      if (identityService.shouldSync(profile.last_identity_sync_at)) {
        void identityService.refresh(userId).then(async () => {
          const { data: refreshed } = await supabase.from('profiles').select('*').eq('id', userId).single();
          if (refreshed) {
            const u = applyVerified(mapProfileToUser(refreshed as Profile));
            setUser((prev) => (prev?.id === userId ? u : prev));
          }
        });
      }
      const mapped = applyVerified(mapProfileToUser(profile as Profile));
      if (!mapped.createdAt && supabaseUser?.created_at) {
        mapped.createdAt = supabaseUser.created_at;
      }
      return mapped;
    } catch (e) {
      console.warn('Profile fetch failed:', e);
      return fallback();
    }
  }, []);

  useEffect(() => {
    let settled = false;
    const settle = () => {
      if (!settled) {
        settled = true;
        setLoading(false);
      }
    };

    const timeout = setTimeout(() => {
      console.warn('Auth init timed out — continuing as guest');
      settle();
    }, 2500);

    /** When true, first profile fetch completion calls settle() so UI does not treat stub user as final. */
    const hydrateUser = (newSession: Session | null, awaitingInitialSettle = false) => {
      const bumpSettle = () => {
        if (awaitingInitialSettle) settle();
      };
      if (newSession?.user) {
        const prev = userRef.current;
        const sameAccount = prev?.id === newSession.user.id;
        // Session refresh / duplicate INITIAL_SESSION: do not replace loaded (or stub) user with a
        // fresh stub — that cleared is_admin / is_manager briefly and showed Operations "access denied".
        if (sameAccount && prev) {
          void fetchProfile(newSession.user.id, newSession.user)
            .then((full) => {
              if (full) setUser(full);
            })
            .finally(bumpSettle);
          return;
        }
        const stub = mapProfileToUser(stubProfileFromSupabaseUser(newSession.user));
        stub.emailVerified = !!newSession.user.email_confirmed_at;
        setUser(stub);
        void fetchProfile(newSession.user.id, newSession.user)
          .then((full) => {
            if (full) setUser(full);
          })
          .finally(bumpSettle);
      } else {
        setUser(null);
        bumpSettle();
      }
    };

    supabase.auth
      .getSession()
      .then(({ data: { session: currentSession } }) => {
        setSession(currentSession);
        hydrateUser(currentSession, true);
      })
      .catch((err) => {
        console.warn('Auth session retrieval failed, continuing as guest:', err?.message);
        settle();
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      if (newSession?.user && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
        void claimReferralForSessionUser(newSession.user);
        void presenceService.heartbeat();
      }
      const awaitProfile =
        !newSession ||
        event === 'INITIAL_SESSION' ||
        event === 'SIGNED_IN' ||
        event === 'USER_UPDATED';
      hydrateUser(newSession, awaitProfile);
    });

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signUp = async (
    email: string,
    password: string,
    username: string,
    displayName: string,
    accountType: 'seller' | 'collector' | 'curator' | 'creator' = 'collector',
    referralCode: string | null = null
  ): Promise<{ error: string | null; needsEmailConfirmation?: boolean }> => {
    const uname = normalizeUsername(username);
    const fmtErr = validateUsernameFormat(uname);
    if (fmtErr) return { error: fmtErr };

    if (await isUsernameTaken(uname)) {
      return { error: USERNAME_TAKEN_MESSAGE };
    }

    const display = (displayName || '').trim() || uname;
    const ref = (referralCode || '').trim().toUpperCase();

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: getAuthCallbackUrl(),
        data: {
          username: uname,
          display_name: display,
          ...(ref ? { referral_code: ref } : {}),
        },
      },
    });

    if (error) {
      const low = error.message.toLowerCase();
      if (low.includes('duplicate') || low.includes('unique') || low.includes('already') || low.includes('username')) {
        return { error: USERNAME_TAKEN_MESSAGE };
      }
      return { error: mapAuthEmailError(error.message) };
    }

    if (data.user) {
      const isSeller = accountType === 'seller';
      const roleBadge = accountType === 'seller' ? 'Seller'
        : accountType === 'curator' ? 'Curator'
        : accountType === 'creator' ? 'Creator'
        : 'Collector';

      const { error: upErr } = await supabase.from('profiles').update({
        username: uname,
        display_name: display,
        avatar_url: `https://ui-avatars.com/api/?name=${encodeURIComponent(display)}&background=random&color=fff&size=200`,
        account_type: accountType,
        is_seller: isSeller,
        badges: [roleBadge],
      }).eq('id', data.user.id);

      const mapped = mapProfileUsernameError(upErr);
      if (mapped) return { error: mapped };
      if (upErr) return { error: upErr.message };

      if (isSeller) {
        await supabase.from('seller_rep_scores').insert({
          seller_id: data.user.id,
          overall_score: 0,
          tier: 'Bronze',
        });
      }

      if (ref && data.session) {
        await claimReferralCode(ref);
      }
    }

    const needsEmailConfirmation = Boolean(data.user && !data.session);
    return { error: null, needsEmailConfirmation };
  };

  const signIn = async (
    email: string,
    password: string
  ): Promise<{ error: string | null; errorCode?: string | null }> => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return { error: 'Enter your email address.' };

    const { error } = await supabase.auth.signInWithPassword({
      email: trimmedEmail,
      password,
    });
    if (error) {
      return {
        error: error.message,
        errorCode: (error as { code?: string }).code ?? null,
      };
    }
    return { error: null };
  };

  const resendSignupConfirmation = async (email: string): Promise<{ error: string | null }> => {
    const trimmed = email.trim();
    if (!trimmed) return { error: 'Enter your email address.' };
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: trimmed,
      options: { emailRedirectTo: getAuthCallbackUrl() },
    });
    if (error) return { error: mapAuthEmailError(error.message) };
    return { error: null };
  };

  const signOut = async () => {
    await presenceService.markOffline();
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  const updateProfile = async (
    updates: Partial<Profile>
  ): Promise<{ error: string | null }> => {
    if (!session?.user) return { error: 'Not authenticated' };

    const payload: Record<string, unknown> = { ...updates };
    if (updates.username !== undefined && updates.username !== null) {
      const u = normalizeUsername(String(updates.username));
      const fmtErr = validateUsernameFormat(u);
      if (fmtErr) return { error: fmtErr };
      if (await isUsernameTaken(u, session.user.id)) {
        return { error: USERNAME_TAKEN_MESSAGE };
      }
      payload.username = u;
    }

    const { error } = await supabase
      .from('profiles')
      .update(payload as Partial<Profile>)
      .eq('id', session.user.id);

    const mapped = mapProfileUsernameError(error);
    if (mapped) return { error: mapped };
    if (error) return { error: error.message };

    // Refresh local user state
    const profile = await fetchProfile(session.user.id, session.user);
    if (profile) setUser(profile);
    return { error: null };
  };

  const refreshProfile = async () => {
    if (session?.user) {
      const profile = await fetchProfile(session.user.id, session.user);
      if (profile) setUser(profile);
    }
  };

  const setAvailability = async (status: User['availabilityStatus']) => {
    if (!session?.user) return;
    await supabase.from('profiles').update({ availability_status: status }).eq('id', session.user.id);
    if (status === 'online' || status === 'open_to_chat') {
      const res = await presenceService.heartbeat();
      if (res.ok) {
        setUser((prev) =>
          prev
            ? { ...prev, availabilityStatus: status, lastSeenAt: new Date().toISOString() }
            : null,
        );
        return;
      }
    }
    setUser((prev) => (prev ? { ...prev, availabilityStatus: status } : null));
  };

  const checkUsernameAvailable = async (username: string): Promise<boolean> => {
    const u = normalizeUsername(username);
    const fmt = validateUsernameFormat(u);
    if (fmt) return false;
    return !(await isUsernameTaken(u, session?.user?.id));
  };

  React.useEffect(() => {
    const onProf = () => {
      if (session?.user) {
        void fetchProfile(session.user.id, session.user).then((p) => {
          if (p) setUser(p);
        });
      }
    };
    window.addEventListener('d88-profile-prefs-changed', onProf);
    return () => window.removeEventListener('d88-profile-prefs-changed', onProf);
  }, [session?.user?.id, session?.user, fetchProfile]);

  React.useEffect(() => {
    const onHeartbeat = () => {
      setUser((prev) => {
        if (!prev) return null;
        const keepManual =
          prev.availabilityStatus === 'busy' || prev.availabilityStatus === 'away';
        return {
          ...prev,
          lastSeenAt: new Date().toISOString(),
          availabilityStatus: keepManual ? prev.availabilityStatus : 'online',
        };
      });
    };
    window.addEventListener('d88-presence-heartbeat', onHeartbeat);
    return () => window.removeEventListener('d88-presence-heartbeat', onHeartbeat);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoggedIn: !!user,
        loading,
        signUp,
        signIn,
        signOut,
        updateProfile,
        refreshProfile,
        setAvailability,
        checkUsernameAvailable,
        resendSignupConfirmation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
